import angular, { IScope, IPromise } from 'angular'
import ngRedux from 'ng-redux'
import trim from 'lodash/trim'
import last from 'lodash/last'

import VendNumber, {
  vn,
  subtract,
  round,
  multiply,
  isFinite,
} from 'vend-number'

import {
  VdTracking,
  VdPerformanceTracking,
  VdFeatureManager,
} from '@vend/vend.js'
import {
  ApiCustomer,
  ApiRetailerPaymentType,
  ApiCustomerGroup,
  ApiTransaction,
} from '@vend/types'
import uuid from '@vend/vend-uuid'
import {
  PAYMENT_TYPES,
  isAmountEligibleForLSPayLink,
} from '@vend/business-components'
import { trackError } from '@vend/utilities'
import type {
  ModalObject,
  VdToastNotificationService,
} from '../../vend-ui/types'
import { KLARNA_PAYMENT_TYPES } from '../../components/payments/integrated-payment/integrated-payment-types.constant'
import { EVENT, EVENTS, PERMISSIONS, DebounceOptions } from '../../constants'
import { isCorrectlyRounded } from '../../components/helpers/isCorrectlyRounded'
import {
  getAllPaymentsForPaymentType,
  getMainApiTender,
  getPaymentIds,
  getSurchargeRuleFromTenders,
  getTotalPaymentAmountFromTenders,
  getTotalSurchargeAmountFromTenders,
  getTotalTipAmountFromTenders,
  hasCashDiscountingServiceFee,
  hasPaymentsForOtherPaymentType,
  isABPAddedButNotPaid,
  removeActiveTransactionDetails,
  roundCash,
} from '../../functions/payments'
import { SaleStatus } from '../../models/sale.constants'
import {
  isEnabledPaymentTypeForOutlet,
  isIpadOnly,
} from '../../functions/payment-types/payment-types'
import { SaleManager } from '../../managers/sale-manager'
import { paymentTypesStore } from '../../factories/database/payment-types-store'
import { RegisterManager } from '../../managers/register-manager'
import { SurchargeTenderProperties } from '../../models/sale'
import { STATES } from '../../../../lib/routes'
import {
  $log,
  $q,
  $state,
  $stateParams,
  $rootScope,
  $window,
} from '../../ng-compat/imports'
import { TrainingModeManager } from '../../managers/training-mode-manager'
import { UserManager } from '../../managers/user-manager'
import { WebRegisterStatusManager } from '../../managers/web-register-status-manager'
import {
  PaymentManager,
  PrintReceiptEvent,
  ReceiptTransaction,
} from '../../managers/payment-manager'
import { LSPAYModalOptions } from '../../components/payments/lspay-payment-modal/helpers/lspay.types'
import { shouldAllowUnreferencedRefunds } from '../../components/payments/lspay-payment-modal/helpers/refund'
import { RetailerSettingsManager } from '../../managers/retailer-settings-manager'
import {
  shouldDisableEconduitPayment,
  shouldUseDirectEconduit,
} from '../../functions/payment-types/econduit'
import {
  hasKlarnaPayments,
  isKlarnaPaymentType,
  shouldDisableKlarnaPayment,
} from '../../functions/payment-types/klarna'
import { shouldDisableVCloudPayment } from '../../functions/payment-types/vcloud'
import { shouldPreventOtherPayments } from '../../functions/payment-types/other-payment'
import { CashPaymentInformation } from '../../components/payments/cash-payment-modal/CashPaymentModal'
import {
  canApplySurcharge,
  getTipEligibleAmount,
  requireUnreferencedRefund,
} from '../../functions/payment-types/lspay'
import {
  isReturnedLSPayESale,
  preparePaymentTypesForLSpayEcomRefund,
} from '../../functions/payment-types/lspayEcom'
import {
  isReturnedEcomPayPalSale,
  isPayPalEcomPaymentType,
} from '../../functions/payment-types/paypalEcom'
import { CustomerBalancesManager } from '../../managers/customer-balances-manager'
import {
  isReturn,
  isLayby,
  isLegacyServiceSale,
  isValidCustomer,
  getPaymentsTotal,
  getOutstandingBalance,
  isAccountBalancePaymentAdded,
  isAccountBalancePaymentPaid,
  isMarkedAsUnfulfilled,
  isPartialPaidSale,
  isOnAccountLimitReached,
  hasPayments,
  isUnpaidSale,
  isBlindReturn,
} from '../../functions/sale'
import { hasServiceOrder } from '../../functions/service'
import {
  hasValidCustomerWithLoyalty,
  isOnAccountLimitDisabled,
  isOnAccountLimitUnlimited,
} from '../../functions/customer'
import { containsGiftCard } from '../../functions/lineItem'
import {
  generateLoyaltyClaimCode,
  hideAttachCustomerTooltip,
  ReduxCurrentSale,
  saleSubmittedEarly,
  selectCurrentSale,
  selectCurrentSaleAsSale,
  selectLineItems,
} from '../../state/slices/currentSale/currentSaleSlice'
import { LineItem } from '../../models/line-item'
import { RootState } from '../../state/store'
import { getFeatureFromCache } from '../../util/feature-cache'
import {
  extractExternalPaymentData,
  getAllExternalTransactionIds,
  shouldCheckSwiperMaintenance,
  trackIntegratedPaymentStep,
} from '../../functions/payment-types/integrated'
import { EcomRefundModalOptions } from '../../components/payments/ecom-refund-modal/helpers/types'
import { checkSwiperMaintenance } from '../../components/payments/payments-resource.service'

const SWIPER_SOURCE_NAME = 'Swiper'

const LOYALTY_PAYMENT_OVER_BALANCE_MESSAGE = `Amount tendered exceeds payment balance.
    Please change the amount to pay with Loyalty, or choose another payment type.`

const STORE_CREDIT_PAYMENT_OVER_BALANCE = `Amount tendered exceeds payment balance.
    Please change the amount to pay with Store Credit, or choose another payment type.`

const STORE_CREDIT_INVALID_TENDERED_AMOUNT_ENTERED = `Invalid tendered amount.
    Please change the amount to tender, or choose another payment type.`

export const MAX_TENDERED_AMOUNT = 10_000_000_000
interface PaymentButtonConfig {
  id: 'loyalty' | 'storeCredit'
  label: string
  action: () => void
  isDisabled?: boolean
}

// When attached via onunload, this gives the user a chance to cancel before leaving/refreshing
const confirmQuit: EventListener = e => {
  // TS does not like `returnValue`, but without it no confirmation popup shows.
  // @ts-ignore
  e.returnValue = 'Please do not refresh the page if a transaction is ongoing'
  e.preventDefault()
}

/**
 * =======================================
 * PAYMENT CONTROLLER
 * =======================================
 */
class PaymentCtrl {
  public amountTendered: number | string
  public currentBalance?: number
  public readonly customerErrorTemplate: string
  public customerGroup?: ApiCustomerGroup
  public customerPaymentOptions?: PaymentButtonConfig[]
  public readonly customerSource: any
  public hasValidTenderedAmount?: boolean
  public readonly isStoreCreditEnabled: boolean
  public loyaltyButtonConfig?: PaymentButtonConfig
  public paymentFormCtrl: any
  public paymentProcessing = false
  public paymentTypes?: ApiRetailerPaymentType[]
  public lspayPaymentType?: ApiRetailerPaymentType
  public paypalEcomPaymentType?: ApiRetailerPaymentType
  public sale: ReduxCurrentSale
  public readonly isBlockedByAccountingIntegrations: boolean
  public paymentsTotal: number
  public debounceOptions: DebounceOptions | undefined
  public readonly paymentTypeDisabledTooltip = {
    template: `
    <vd-tooltip>
      <div class="vd-pa3">
        <p class="vd-text-label vd-mb3"><i class="fa fa-info-circle vd-mr1"></i> You can’t use this payment type while offline.</p>
        <p class="vd-p">You need to be connected to the internet to take<br> payments using this payment type.</p>
      </div>
    </vd-tooltip>
  `,
  }

  public vendNumberOptions: {
    updateOnChange: boolean
    min?: number
    max: number
  } = {
    updateOnChange: true,
    max: MAX_TENDERED_AMOUNT,
  }

  private lineItems: LineItem[]
  private balance: number
  private closePaymentScreen: () => void
  private readonly hasXeroEnabled: boolean
  private readonly hasQboEnabled: boolean
  private readonly shouldEnforceOnAccountLimit: boolean
  // To handle LSPay ecom refund, verify the current sale is LSPayOnline  return and feature flag is enabled
  private readonly isLSPayEcomRefund: boolean
  // To handle Paypal ecom refund, verify the current sale is Paypal Standard (Online) return and feature flag is enabled
  private readonly isPayPalEcomRefund: boolean
  private readonly shouldIgnoreAccountingIntegrations: boolean
  private readonly hasAccountingIntegrations: boolean
  private storeCreditButtonConfig?: PaymentButtonConfig

  private currencyAdjustmentPaymentType?: ApiRetailerPaymentType
  private loyaltyPaymentType?: ApiRetailerPaymentType
  private roundingPaymentType?: ApiRetailerPaymentType
  private storeCreditPaymentType?: ApiRetailerPaymentType
  private whenPaymentTypesFetched?: IPromise<void>

  constructor(
    private readonly $scope: IScope,
    private readonly vdToastNotificationService: VdToastNotificationService,
    private readonly saleManager: SaleManager,
    private readonly wrSaleStatusFilter: any,
    private readonly balancesGiftCardsModal: any,
    private readonly bigcommerceGiftCertsModal: any,
    private readonly paymentManager: PaymentManager,
    private readonly retailerSettingsManager: RetailerSettingsManager,
    private readonly userManager: UserManager,
    private readonly trainingModeManager: TrainingModeManager,
    private readonly vdTracking: VdTracking,
    private readonly customerModals: any,
    private readonly webRegisterStatusManager: WebRegisterStatusManager,
    private readonly vdEscapeStack: any,
    private readonly customerBalancesManager: CustomerBalancesManager,
    private readonly registerManager: RegisterManager,
    private readonly paymentModalService: any,
    private readonly Confirm: any,
    private readonly vdWindowDelegate: any,
    private readonly vdPerformanceTracking: VdPerformanceTracking,
    private readonly customerManager: any,
    private readonly paymentsResourceService: any,
    private readonly vdFeatureManager: VdFeatureManager,
    customerSuggestions: any,
    private readonly $ngRedux: ngRedux.INgRedux
  ) {
    'ngInject'

    this.sale = selectCurrentSale(this.$ngRedux.getState())
    this.lineItems = selectLineItems(this.$ngRedux.getState())

    const disconnectRedux = this.$ngRedux.connect((state: RootState) => ({
      sale: selectCurrentSale(state),
      lineItems: selectLineItems(state),
    }))(this)

    $scope.$on('$destroy', disconnectRedux)

    // Mandatory initialisation in construct
    this.amountTendered = 0
    this.balance = 0
    this.setTenderedAmount()
    this.debounceOptions = this.saleManager.debounceOptions

    /**
     * Close the payment screen and return to the current sale. Do not close the sale.
     */
    this.closePaymentScreen = () => {
      $state.go(STATES.SALE)
    }

    if (this.trainingModeManager.trainingModeEnabled) {
      // Don't allow new customers to be created in Training Mode
      this.customerSource = customerSuggestions.getSource({ readOnly: true })
    } else {
      this.customerSource = customerSuggestions.getSource({
        create: ({ query }: { query: string }) => {
          return this.customerModals
            .createCustomer({ query })
            .then((customer: ApiCustomer) => this.addCustomer(customer))
        },
      })
    }
    // Replaces the default error message for the "add customer to sale" autocomplete
    this.customerErrorTemplate = customerSuggestions.customerErrorTemplate

    if (
      isValidCustomer(this.sale.customer) &&
      !this.sale.hideAttachCustomerTooltip
    ) {
      this.$ngRedux.dispatch(hideAttachCustomerTooltip(true))
      this.saleManager.saveSale()
    }

    /**
     * Determine whether store credit is enabled on the retailer
     */
    this.isStoreCreditEnabled = retailerSettingsManager.isStoreCreditEnabled()

    /**
     * Determine whether Xero integration is enabled on the retailer
     */
    this.hasXeroEnabled = retailerSettingsManager.isXeroIntegrationEnabled()
    this.hasQboEnabled = this.vdFeatureManager.get('has_qbo')
    this.shouldIgnoreAccountingIntegrations = this.vdFeatureManager.get(
      'pufs_ignore_accounting_integrations'
    )
    this.hasAccountingIntegrations = this.hasXeroEnabled || this.hasQboEnabled

    this.isBlockedByAccountingIntegrations = this
      .shouldIgnoreAccountingIntegrations
      ? false
      : this.hasAccountingIntegrations

    this.shouldEnforceOnAccountLimit = this.vdFeatureManager.get(
      'enable_on_account_limit_enforcement'
    )
    this.isLSPayEcomRefund =
      this.vdFeatureManager.get('lspay_eseries_refunds_on_xseries') &&
      isReturnedLSPayESale(this.sale)
    this.isPayPalEcomRefund = isReturnedEcomPayPalSale(this.sale)
    this.paymentsTotal = getPaymentsTotal(this.sale.payments)
    this.init()
  }

  // ============================== Public =============================== //

  /**
   * Helper to change the placeholder text for the autocomplete component
   */
  public getAddCustomerPlaceholder() {
    let placeholder = 'Add a customer to this sale.'

    if (getOutstandingBalance(this.sale) > 0 || this.isStoreCreditEnabled) {
      placeholder = 'Add a customer to pay with the following options:'
    }

    return placeholder
  }

  /**
   * Add a customer to the sale via the "Add customer to sale" autocomplete.
   */
  public addCustomer(customer: ApiCustomer) {
    this.saleManager
      .addCustomer(customer)
      .then(() => this.buildPaymentOptions())
      .then(() => this.resolveCustomerGroup(this.sale.customer!))
      .then(() => this.setTenderedAmount())
  }

  /**
   * Respond to a customer being added to the sale via the autocomplete input.
   *
   * @param {Suggestion} result
   *        The chosen Customer suggestion
   */
  public onCustomerResultSelected(result: { customer: ApiCustomer }) {
    const { customer } = result
    this.addCustomer(customer)
  }

  /**
   * Determine whether loyalty is enabled on the retailer.
   *
   * @return {Boolean} true if loyalty is enabled on the retailer, false otherwise
   */
  public isLoyaltyEnabled() {
    return this.retailerSettingsManager.isLoyaltyEnabled()
  }

  /**
   * Determine whether the amount is valid for payment link
   *
   * @return {Promise<boolean>} A promise that resolves to boolean which decides to continue with current amount
   */

  public async checkPaymentLinkLimitToContinue(
    amount: number | string
  ): Promise<boolean> {
    const { id: activeOutletId } = this.registerManager.getActiveOutlet()
    try {
      const isEligible: boolean = await this.paymentManager.retrievePaymentLinkAvailability(
        activeOutletId
      )
      if (isEligible) {
        const shouldContinue: boolean = await this.paymentModalService.payLinkLimit(
          vn(amount).toNumber()
        )
        return shouldContinue
      }
      return true
    } catch (error) {
      $log.warn('[Pay screen]', error)
      return true
    }
  }

  /**
   * Process layby and onaccount sales.
   *
   * @param status
   */
  public async laybyOnAccountSale(
    status: SaleStatus.LAYBY_SALE | SaleStatus.ACCOUNT_SALE
  ) {
    if (this.paymentProcessing) {
      return
    }

    let laybyOnAccountSaleDeferred
    this.paymentProcessing = true

    if (status === SaleStatus.LAYBY_SALE) {
      laybyOnAccountSaleDeferred = $q.when(this.saleManager.laybySale())
    } else {
      // status === SaleStatus.ACCOUNT_SALE

      if (
        this.vdFeatureManager.get('lspay_payment_links') &&
        !(isReturn(this.sale) && vn(this.amountTendered).lte(0)) &&
        !isAmountEligibleForLSPayLink(this.amountTendered)
      ) {
        const shouldContinue: boolean = await this.checkPaymentLinkLimitToContinue(
          this.amountTendered
        )
        if (!shouldContinue) {
          this.paymentProcessing = false
          return
        }
      }

      laybyOnAccountSaleDeferred = $q.when(this.saleManager.onAccountSale())
    }

    // Set the loyalty claim code for the sale
    this.$ngRedux.dispatch(generateLoyaltyClaimCode())

    laybyOnAccountSaleDeferred.then(() => {
      this.vdTracking.trackUserEvent({
        eventName: 'Open Sale Confirmation',
        metadata: {
          id: this.sale.localId,
        },
      })

      this.paymentProcessing = false
      this.goConfirmation()
    })
  }

  /**
   * Process a payment depending on the chosen payment type
   *
   * Heads up! this function returns  ES6 promise that might have wrap angular promise $q,
   * you should not await on this function as there may be side effects.
   */
  public async pay(paymentType: ApiRetailerPaymentType) {
    let paymentDeferred

    if (this.paymentProcessing) {
      return
    }

    if (this.paymentFormCtrl.$invalid) {
      return
    }

    // stop the timing when the user selects a payment type
    const paymentTypeMeasure = this.vdPerformanceTracking.stop(
      'select-payment-type-process'
    )

    this.vdTracking.trackUserEvent({
      eventName: 'Payment type selected',
      metadata: {
        amount: this.amountTendered,
        registerSaleId: this.sale.localId,
        startTime: paymentTypeMeasure?.startTime || null,
        duration: paymentTypeMeasure?.duration || null,
        paymentTypeName: paymentType.name,
        paymentTypeId: paymentType.type_id,
        retailerPaymentTypeId: paymentType.id,
        isQuickPay: false,
        quickPayFeatureFlag: this.vdFeatureManager.get(
          'lspay_pay_with_quick_pay_experiment'
        ),
      },
    })

    this.paymentProcessing = true

    if (this.trainingModeManager.trainingModeEnabled) {
      this.vdTracking.trackUserEvent({
        eventName: this.trainingModeManager.currentTourType!,
        metadata: {
          step: 'Selected Payment Type',
          paymentType: paymentType.name,
        },
      })
    } else {
      this.vdPerformanceTracking.start('payment-tender')
    }

    /**
     * Swiper integrated PTs should be blocked during a scheduled swiper db upgrade
     * it is controlled by a feature flag
     * details: https://vendhq.atlassian.net/browse/PAY-1690
     */
    if (shouldCheckSwiperMaintenance(paymentType)) {
      const underMaintenance = await checkSwiperMaintenance(paymentType.type_id)
      if (underMaintenance) {
        this.showSwiperMaintenanceModal()
        this.paymentProcessing = false
        return
      }
    }

    window.addEventListener('beforeunload', confirmQuit)

    switch (paymentType.type_id) {
      case PAYMENT_TYPES.WORLDPAY:
      case PAYMENT_TYPES.WORLDPAY_TRIPOS:
        paymentDeferred = this.payWithSwiper(paymentType)
        break
      case PAYMENT_TYPES.LSPAY:
        paymentDeferred = this.payWithLSPay(paymentType)
        break

      case PAYMENT_TYPES.LIGHTSPEED_ECOM:
        paymentDeferred = this.refundWithEcom(paymentType)
        break

      // Vend
      case PAYMENT_TYPES.GIFT_CARD:
        paymentDeferred = this.payWithGiftCard(paymentType)
        break
      case PAYMENT_TYPES.LOYALTY:
        paymentDeferred = this.payWithLoyalty(paymentType)
        break
      case PAYMENT_TYPES.STORE_CREDIT:
        paymentDeferred = this.payWithStoreCredit(paymentType)
        break

      case PAYMENT_TYPES.CASH:
      case PAYMENT_TYPES.CASH_CONCEALED:
      case PAYMENT_TYPES.CURRENCY:
        paymentDeferred = this.payWithCash(paymentType)
        break

      // Channels
      case PAYMENT_TYPES.BIGCOMMERCE_GIFT_CERT:
        paymentDeferred = this.payWithBigCommerceGiftCert(paymentType)
        break

      // General
      default: {
        const paymentTypeConfig = paymentType.config
        const hasPaymentTypeUrl =
          paymentTypeConfig && trim(paymentTypeConfig.url)

        if (hasPaymentTypeUrl) {
          if (
            shouldUseDirectEconduit(paymentType) &&
            this.vdFeatureManager.get('direct_econduit')
          ) {
            paymentDeferred = this.payWithSwiper(paymentType)
          } else {
            // All integrated payment types will be here due to the config url
            paymentDeferred = this.payWithIntegration(paymentType)
          }
        } else {
          paymentDeferred = this.completePayment({ paymentType })
        }
      }
    }

    return paymentDeferred.finally(() => {
      this.paymentProcessing = false
      window.removeEventListener('beforeunload', confirmQuit)
    })
  }

  /**
   * Evaluates the current sale and determines if the tendered amount should be disabled.
   *
   * @return {Boolean} True if the tendered amount input field should be disabled, false otherwise.
   */
  public shouldDisableTenderedAmount() {
    const klarnaPaymentsPresent = hasKlarnaPayments({
      payments: this.sale.originalSalePayments,
    })

    // Disable user change tendered amount for buy now pay later during return sale
    return isReturn(this.sale) && klarnaPaymentsPresent
  }

  /**
   * Check if the tendered amount should be focussed
   *
   * @returns {Boolean} True if the tendered amount input field should be focussed, false otherwise.
   */
  public shouldFocusTenderedAmount() {
    return Boolean(!$stateParams.lspayQuickPaySelected)
  }

  /**
   * Responsible for determining whether webregister is in offline only mode
   * and if the payment type in question should be disabled accordingly.
   *
   * @param {Payment} paymentType
   *        The payment type to evaluate
   *
   * @return {Boolean} True if webregister is offline and payment type is online only, false otherwise.
   */
  public disableOnlinePaymentInOfflineMode(
    paymentType: ApiRetailerPaymentType
  ) {
    const isOnlineOnly =
      paymentType.payment_type && paymentType.payment_type.online_only
    const isInOfflineMode = !this.webRegisterStatusManager.isOnline()

    return isOnlineOnly && isInOfflineMode
  }

  /**
   * Sends a UBE event before taking user to the confirmation screen
   * @deprecated This method will be removed once payment panel is fully migrated to react.
   * Don't forget to update SaveSale.tsx if you change this method.
   */
  public saveSale() {
    this.vdTracking.trackUserEvent({
      eventName: 'Clicked: Save Sale',
      metadata: {
        id: this.sale.localId,
        paymentsTotal: getPaymentsTotal(this.sale.payments),
        saleTotal: this.sale.finals.inclusivePrice,
      },
    })
    $q.when(this.saleManager.closeSale()).then(() => this.goConfirmation()) // closeSale returns vanila promise so we don't want angular to not know about it
  }

  /**
   * Nav to sale confirmation screen
   */
  public goConfirmation() {
    if (this.saleManager.shouldSubmitSaleEarly()) {
      this.$ngRedux.dispatch(saleSubmittedEarly())
      this.saleManager.submitSale()
    }
    $state.go(STATES.SALE_CONFIRMATION)
  }

  /**
   * Responsible for disabling payment buttons based on their individual requirements.
   *
   * @param {Payment} paymentType
   *        The payment type to evaluate
   *
   * @return {Boolean} True if the payment should be disabled, false otherwise.
   */
  public disablePayment(paymentType: ApiRetailerPaymentType): boolean {
    if (this.disableOnlinePaymentInOfflineMode(paymentType)) {
      return true
    }

    if (!this.hasValidTenderedAmount) {
      return true
    }

    if (
      shouldUseDirectEconduit(paymentType) &&
      this.vdFeatureManager.get('direct_econduit')
    ) {
      return shouldDisableEconduitPayment(paymentType, this.sale)
    }

    if (isKlarnaPaymentType(paymentType.type_id)) {
      return shouldDisableKlarnaPayment(
        this.amountTendered,
        selectCurrentSale(this.$ngRedux.getState()) // FIXME: replace this with this.sale again once payment controller connectd sale to redux
      )
    }

    switch (paymentType.type_id) {
      case PAYMENT_TYPES.LSPAY: {
        // 0 amount cannot be processed by lspay
        if (vn(this.amountTendered).eq(0)) {
          return true
        }
        // If current transaction requires unref refund but unref refund is not enabled for current store, disable lspay
        return (
          requireUnreferencedRefund(this.amountTendered, this.sale) &&
          !shouldAllowUnreferencedRefunds(
            this.vdFeatureManager.get('lspay_unreferenced_refund'),
            this.vdFeatureManager.get(
              'lspay_international_unreferenced_refunds'
            ),
            this.registerManager.getActiveOutlet().currency
          )
        )
      }
      case PAYMENT_TYPES.LIGHTSPEED_ECOM:
        if (vn(this.amountTendered).eq(0)) {
          return true
        }
        break
      case PAYMENT_TYPES.VCLOUD:
        return shouldDisableVCloudPayment(this.amountTendered, this.sale)
      case PAYMENT_TYPES.BIGCOMMERCE_GIFT_CERT:
        // We don't allow returns of BigCommerce Gift Certs at the moment.
        // This is because it's not implemented rather than any specific technical reason.
        if (vn(getOutstandingBalance(this.sale)).lte(0)) {
          return true
        }
        break
      case PAYMENT_TYPES.GIFT_CARD:
        if (vn(getOutstandingBalance(this.sale)).lte(0)) {
          return true
        } else if (
          isAccountBalancePaymentAdded(this.sale.payAccountBalance) &&
          !isAccountBalancePaymentPaid(this.sale.payAccountBalance)
        ) {
          // Cannot make an account balance payment with a gift card as both the sale and ABP will fail.
          return true
        }
        break
      case PAYMENT_TYPES.PAYJUNCTION:
        // we don’t actually support partial refunds for integrated payments right now when the refund is tokenised.
        // If it’s a blind refund then we can allow the amount each time, the consumer chooses the card.
        // But if it’s based on original payment ID then currently we prevent it (we do this for Square/iZettleso on)
        // In that case, we’ll do the same thing for PayJunction
        if (isReturn(this.sale) && this.sale.originalSalePayments.length > 1) {
          return true
        }
    }

    return shouldPreventOtherPayments(this.amountTendered, this.sale)
  }

  /**
   * Process an integrated payment transaction.
   *
   * @param {PaymentType} paymentType
   *        The payment type of this payment.
   *
   * @return {Promise} A promise for the payment integration payment modal
   */
  public payWithIntegration(paymentType: ApiRetailerPaymentType) {
    const register = this.registerManager.getActiveRegister()
    const activeOutlet = this.registerManager.getActiveOutlet()
    const registerId = register && register.id
    const currency = activeOutlet.currency

    this.vdEscapeStack.deregister(this.closePaymentScreen)

    if (isReturn(this.sale)) {
      const originalRegisterSalePaymentIds = getAllPaymentsForPaymentType({
        payments: this.sale.originalSalePayments,
        paymentTypeId: paymentType.type_id,
      }).map(payment => payment.id)

      if (originalRegisterSalePaymentIds.length > 0) {
        return this.paymentsResourceService
          .getTransactions({
            registerId,
            registerSalePaymentIds: originalRegisterSalePaymentIds,
          })
          .then((transactions: any) => {
            if (transactions && transactions.length > 0) {
              const externalTransactionIds = getAllExternalTransactionIds(
                transactions
              )
              return this._payWithIntegration({
                currency,
                paymentType,
                registerId,
                externalTransactionIds,
                registerSalePaymentIds: originalRegisterSalePaymentIds,
              })
            }
            return this._payWithIntegration({
              currency,
              paymentType,
              registerId,
            })
          })
          .catch((error: any) => {
            trackError('[Payment] could not get payment transactions.', {
              data: { error },
            })
            return this._payWithIntegration({
              currency,
              paymentType,
              registerId,
            })
          })
      }
    }

    return this._payWithIntegration({ currency, paymentType, registerId })
  }

  /**
   * Process an integrated payment transaction.
   *
   * @param {Object} params
   *
   *        @param {PaymentType} params.paymentType
   *               The payment type of the payment.
   *
   *        @param {String} params.registerId
   *               The id of the current register.
   *
   *        @param {Array} params.externalTransactionIds
   *               The id of the original register sale external transaction ids. Required for refunds only.
   *
   * @return {Promise} A promise for the payment integration payment modal
   */
  private _payWithIntegration({
    currency,
    paymentType,
    registerId,
    externalTransactionIds,
    registerSalePaymentIds,
  }: {
    currency: string
    paymentType: ApiRetailerPaymentType
    registerId: string
    externalTransactionIds?: any[]
    registerSalePaymentIds?: any[]
  }) {
    return this.paymentModalService
      .integratedPayment({
        currency,
        paymentType,
        amountTendered: this.amountTendered,
        sale: this.sale,
        registerId,
        externalTransactionIds,
        registerSalePaymentIds,
      })
      .then((eventData: any) =>
        this.handleAcceptIntegratedPayment({
          currency,
          paymentType,
          registerId,
          eventData,
        })
      )
      .catch((eventData: any) =>
        this.handleDeclineIntegratedPayment({
          paymentType,
          eventData,
        })
      )
      .finally(() => this.vdEscapeStack.register(this.closePaymentScreen))
  }

  /**
   * Complete a payment. We allow partial payments for both normal and return / negative priced sales
   *
   * @param {PaymentType} paymentType
   *        The payment type of this payment
   *
   * @param {Object} [transactionData]
   *
   *        @param {String} transactionData.source
   *               The descriptive identifier for the transaction, e.g. "Vend Gift Card"
   *
   *        @param {String} transactionData.source_id
   *               The source id for the payment transaction
   *
   *        @param {String} [transactionData.payment_id]
   *               The id for the payment transaction
   *
   *        @param {String} [transactionData.external_transaction_id]
   *               The external id for the payment transaction
   *
   *        @param {String} [transactionData.merchant_id]
   *               The merchant id for the retailer
   *
   *        @param {String} [transactionData.reference_id]
   *               The reference id for the integrator
   *
   *        @param {String} [transactionData.signature_url]
   *               The signature url for the digital signature
   *
   *        @param {String} [transactionData.signature_base64]
   *               The signature base64 for the digital signature base64 data
   *
   *        @param {String} [transactionData.transaction_status]
   *               The status of the transaction. It will be 'APPROVED_SIGNATURE' or 'undefined'
   *
   *        @param {String} [transactionData.auth_code]
   *               The auth code for authorization code provided by authorization network
   *
   *        @param {Object} [transactionData.card]
   *               The card for the payment card detail
   *
   *               @param {String} [transactionData.card.entry_method]
   *               The entry method for the card
   *
   *               @param {String} [transactionData.card.brand]
   *               The brand for the payment card
   *
   *               @param {String} [transactionData.card.type]
   *               The type for the payment card
   *
   *               @param {String} [transactionData.card.last_four]
   *               The last four numbers for the payment card
   *
   *               @param {String} [transactionData.card.cardholder_name]
   *               The cardholder name for payment card
   *
   *        @param {Object} [transactionData.terminal]
   *               The terminal for the payment terminal detail
   *
   *               @param {String} [transactionData.terminal.serial_number]
   *               The serial number for the payment terminal
   *
   *               @param {String} [transactionData.terminal.model]
   *               The mode for the payment terminal
   *
   *        @param {Object} [transactionData.emv]
   *               The emv for the payment card emv detail
   *
   *               @param {String} [transactionData.emv.aid]
   *               The aid for the emv
   *
   *               @param {String} [transactionData.emv.application_label]
   *               The application label for the emv
   *
   *               @param {String} [transactionData.emv.cryptogram_type]
   *               The cryptogram type for the emv
   *
   *               @param {String} [transactionData.emv.cryptogram]
   *               The cryptogram for the emv
   *
   *               @param {Boolean} [transactionData.emv.pin_verified]
   *               The pin verified for the pin
   *
   *               @param {Array} [transactionData.emv.tags]
   *               The Tags for the emv
   *
   * @param  {String} [paymentId]
   *         The id of the current payment.
   *
   * @param  {String} [paymentAmount]
   *         The amount to pay (if different to the amount tendered)
   *
   * @return {Promise} Resolved with the required payment applied to the sale resulting in either closing the sale
   *         or updating the amount tendered with the remaining balance
   */
  public completePayment({
    paymentAmount,
    paymentType,
    transactionData,
    paymentId,
    tipAmount,
    surchargeData,
    changeAmount,
  }: {
    paymentAmount?: any
    paymentType: ApiRetailerPaymentType
    transactionData?: any
    paymentId?: string
    tipAmount?: any
    surchargeData?: SurchargeTenderProperties
    changeAmount?: string
  }) {
    const amountToPay = isFinite(paymentAmount)
      ? paymentAmount
      : this.amountTendered

    return $q
      .when(
        this.saleManager.pay(
          amountToPay,
          paymentType,
          transactionData,
          paymentId,
          tipAmount,
          surchargeData,
          changeAmount
        )
      )
      .then(updatedSale => {
        this.balance = getOutstandingBalance(this.sale)

        const inclusivePrice = updatedSale.finals.inclusivePrice
        const priceIsGreaterThanZero = vn(inclusivePrice).gte(0)
        const priceIsLessThanZero = vn(inclusivePrice).lt(0)
        const balanceIsLessThanZero = vn(this.balance).lte(0)
        const balanceIsZero = vn(this.balance).eq(0)

        if (!this.trainingModeManager.trainingModeEnabled) {
          const completedPayment = last(updatedSale.payments)!
          const { paymentType } = completedPayment

          this.vdPerformanceTracking.stop('payment-tender', {
            metadata: {
              paymentTypeName: paymentType.name,
              paymentTypeId: paymentType.type_id,
              paymentDate: completedPayment.paymentDate,
              retailerPaymentTypeId: paymentType.id,
              salePaymentId: completedPayment.localId,
            },
          } as any)
        }

        const isEligibleForProcessing =
          (priceIsGreaterThanZero && balanceIsLessThanZero) ||
          (priceIsLessThanZero && balanceIsZero)

        if (isEligibleForProcessing) {
          this.paymentManager.updateReceiptMessageOnSaleComplete()
          return this.saleManager
            .closeSale()
            .then(() => {
              this.vdTracking.trackUserEvent({
                eventName: 'Open Sale Confirmation',
                metadata: {
                  id: this.sale.localId,
                },
              })

              removeActiveTransactionDetails()

              return this.goConfirmation()
            })
            .catch(error => {
              // @todo handle payment failure
              trackError('[Payment view] could not complete payment.', {
                data: { error },
              })
            })
        } else {
          removeActiveTransactionDetails()
          this.amountTendered = this.balance
          this.paymentsTotal = getPaymentsTotal(this.sale.payments)
          if (this.shouldEnforceOnAccountLimit) {
            return this.buildCustomerPaymentOptions()
          }
          this.updateStoreCreditButtonDisabledState()
          return this.updateLoyaltyButtonDisabledState()
        }
      })
  }

  /**
   * Process a Gift Card transaction. It will open a modal and start the workflow for redeeming a Gift Card.
   *
   * @param {PaymentType} paymentType
   *        The payment type of this payment
   *
   * @return {Promise} Resolved with the gift card redeem modal and completing the payment when valid or a
   *         toast notification
   */
  public payWithGiftCard(paymentType: ApiRetailerPaymentType) {
    const amountToPay = this.getPaymentAmountForCashDiscounting()

    // A Gift Card payment cannot be negative (return) or 0
    if (vn(amountToPay).lte(0)) {
      return $q.resolve(
        this.vdToastNotificationService.negative({
          message: 'Please enter a positive amount.',
        })
      )
    }

    // TODO: Replace with react modal when this is migrated to PaymentPanel
    return this.balancesGiftCardsModal
      .redeemGiftCard(amountToPay)
      .then((giftCardTransactionResponse: any) => {
        // The gift card may have only partially covered the amountTendered, update the amountTendered with the
        // resolved amount from the transaction.
        return this.completePayment({
          paymentAmount: giftCardTransactionResponse.amountTendered,
          paymentType,
          transactionData: giftCardTransactionResponse,
        })
      })
  }

  /**
   * Process a BigCommerce Gift Cert transaction. It will open a modal and start the workflow for redeeming a Gift Cert.
   *
   * @param {PaymentType} paymentType
   *        The payment type of this payment
   *
   * @return {Promise} Resolved with the gift cert redeem modal and completing the payment when valid or a
   *         toast notification
   */
  public payWithBigCommerceGiftCert(paymentType: ApiRetailerPaymentType) {
    const amountToPay = this.getPaymentAmountForCashDiscounting()

    // A Gift Card payment cannot be negative (return) or 0
    if (vn(amountToPay).lte(0)) {
      return $q.resolve(
        this.vdToastNotificationService.negative({
          message: 'Please enter a positive amount.',
        })
      )
    }

    return this.bigcommerceGiftCertsModal
      .redeemGiftCard(amountToPay)
      .then((giftCardTransactionResponse: any) => {
        // The gift card may have only partially covered the amountTendered, update the amountTendered with the
        // resolved amount from the transaction.
        return this.completePayment({
          paymentAmount: giftCardTransactionResponse.amountTendered,
          paymentType,
          transactionData: giftCardTransactionResponse,
        })
      })
  }

  /**
   * Handle cash payments
   *
   * @param {PaymentType} paymentType
   *        The cash payment type
   *
   * @return {Promise} To resolve the payment with payWithIntegration, payWithRoundedCash, payWithInternationalCurrency
   *         or completePayment depending on the payment type criteria
   */
  public payWithCash(paymentType: ApiRetailerPaymentType) {
    const hasCashDiscounting = this.registerManager.hasCashDiscounting()

    if (paymentType.config) {
      if (trim(paymentType.config.url)) {
        // Some people have cash, but with a URL for some reason. It behaves like an integrated payment for them
        return this.payWithIntegration(paymentType)
      }

      if (paymentType.config.conversionRate) {
        return this.payWithInternationalCurrency(paymentType)
      }
    }

    // There is a possibility for both Cash Discounting and Cash Rounding to be enabled. Cash Discounting trumps paying
    // with rounded cash.
    if (hasCashDiscountingServiceFee(hasCashDiscounting, this.sale)) {
      return this.payWithCashDiscounting(paymentType)
    }

    return this._payWithCash(paymentType)
  }

  /**
   * Handles payments for any paymentTypes that have a conversion rate.
   *
   * @param {PaymentType} paymentType
   *        The international currency payment type
   *
   * @return {Promise} Resolved with completing the payment based on international currency
   */
  public payWithInternationalCurrency(paymentType: ApiRetailerPaymentType) {
    // work out what the equivalent local amount is for this payment
    const localCurrencyAmount = multiply(
      this.amountTendered,
      paymentType.config!.conversionRate!
    )

    const currencyRounding = subtract(localCurrencyAmount, this.amountTendered)

    // add a payment for the difference between international and local currency amounts
    // THEN update the balance to pay
    // THEN complete the payment as if this were a regular cash payment
    return $q
      .when(
        this.saleManager.pay(
          currencyRounding,
          this.currencyAdjustmentPaymentType!
        )
      )
      .then(updatedSale => {
        this.balance = subtract(this.balance, currencyRounding)
      })
      .then(() => this.completePayment({ paymentType }))
  }

  /**
   * Process a cash transaction when rounding is on
   *
   * @param {PaymentType} paymentType
   *        The cash payment type configured for rounding.
   *
   * @return {Promise} Resolved with completing the payment with rounded cash
   */
  private _payWithCash(paymentType: ApiRetailerPaymentType) {
    const amountTendered = vn(this.amountTendered)
    const rounding =
      paymentType.config && paymentType.config.rounding
        ? paymentType.config.rounding
        : '0.01'
    const roundedAmountTendered = roundCash(amountTendered, paymentType)
    const roundedBalance = roundCash(this.balance, paymentType)

    if (
      // Don't skip if it's not correctly rounded
      isCorrectlyRounded(amountTendered, vn(rounding)) &&
      // Skip if it's correctly rounded and 0 (0 dollar cash payment)
      (amountTendered.isZero() ||
        // Skip if it's correctly rounded, positive and greater than the balance
        (amountTendered.gt(0) && amountTendered.gt(this.balance)))
      // Don't ever skip for refunds as we always want to show the modal
    ) {
      // Skip payment modal and finalise payment
      const paymentInformation = {
        remainder: roundedBalance.remainder.toString(),
        cashAmount: amountTendered.toString(),
        changeAmount: amountTendered.gt(this.balance)
          ? amountTendered.minus(this.balance).toFixed(2)
          : undefined,
      }
      return this.completeRoundedCashPayment(paymentInformation, paymentType)
    } else {
      const currencySymbol = this.registerManager.getActiveOutlet()
        .currency_symbol
      // New tender flow modal
      return (
        this.paymentModalService
          .cashPayment(
            currencySymbol,
            roundedAmountTendered,
            roundedBalance,
            rounding
          )
          .then((modal: ModalObject<CashPaymentInformation>) => modal.close)
          .then((paymentInformation: CashPaymentInformation) => {
            return this.completeRoundedCashPayment(
              paymentInformation,
              paymentType
            )
          })
          // Payment modal was closed and no payment was made. Nothing to do except prevent an error.
          .catch(angular.noop)
      )
    }
  }

  /**
   * Process a cash transaction when cash discounting is enabled. Main purpose here is to bring attention to
   * the outstanding discounted cash balance.
   *
   * @param {PaymentType} paymentType
   *
   * @return {Promise} Resolved with completing the payment
   */
  public payWithCashDiscounting(paymentType: ApiRetailerPaymentType) {
    return this.paymentModalService
      .cashDiscountingPayment({
        amountTendered: this.amountTendered,
        balance: this.balance,
        cashDiscountedBalance: this.saleManager.getCashDiscountedOutstandingBalance(),
      })
      .then((selectedAmount: number) => {
        this.amountTendered = selectedAmount
        this.completePayment({ paymentType })
      })
  }

  /**
   * Updates the tendered amount to the minimum ABP amount if it is
   * not already paid
   */
  public updateTenderedAmountToMinimumAbp() {
    if (isABPAddedButNotPaid(this.sale)) {
      this.setTenderedAmount(this.sale.payAccountBalance?.amount)
    }
  }

  // ============================== Private =============================== //

  /**
   * Avoid getting `undefined` on this.amountTendered,
   * validation flow on affects of setting this.amountTendered can finish before we complete payment
   *
   * @param cashAmount
   * @param paymentType
   *
   * @return {Promise} Resolved when the rounded cash payment is complete.
   */
  private tenderRoundedCash(
    cashAmount: number,
    paymentType: ApiRetailerPaymentType,
    changeAmount?: string
  ) {
    return $q(resolve => {
      this.amountTendered = cashAmount

      this.$scope.$applyAsync(() => {
        this.completePayment({ paymentType, changeAmount }).then(resolve)
      })
    })
  }

  /**
   * Wrapper around the completePayment function to ensure rounding remainders get registered
   *
   * @param {Object} paymentInformation
   *        @param {String} remainder
   *               Remainder after rounding
   *
   *        @param {String} cashAmount
   *               Amount tendered in this payment
   *
   * @param paymentType
   *
   * @return {Promise} Resolved when rounded cash payment has been tendered.
   */
  private completeRoundedCashPayment(
    paymentInformation: CashPaymentInformation,
    paymentType: ApiRetailerPaymentType
  ) {
    const { remainder, cashAmount, changeAmount } = paymentInformation

    if (vn(0).eq(remainder)) {
      return this.tenderRoundedCash(
        vn(cashAmount).toNumber(),
        paymentType,
        changeAmount
      )
    } else {
      return $q
        .when(this.saleManager.pay(remainder, this.roundingPaymentType!))
        .then(() => {
          this.balance = subtract(this.balance, remainder)
          return this.tenderRoundedCash(
            vn(cashAmount).toNumber(),
            paymentType,
            changeAmount
          )
        })
    }
  }

  /**
   * Pay with store credit
   *
   * @param {PaymentType} paymentType
   *        The payment type of this payment.
   *
   * @return {Promise} Resolved with completing the payment when valid or a toast notification
   */
  private payWithStoreCredit(paymentType: ApiRetailerPaymentType) {
    const hasNegativeBalance = vn(this.balance).isNeg()
    const amountToPay = this.getPaymentAmountForCashDiscounting()

    // The following adds some store credit specific rules
    // If we encounter a positive balance
    if (!hasNegativeBalance) {
      const availableStoreCredit = this.getAvailableStoreCredit()

      // Do not allow the user to pay with more store credit than the sale balance
      // This avoids the customer cashing out store credit
      if (vn(amountToPay).gt(this.balance)) {
        return $q.resolve(
          this.vdToastNotificationService.negative({
            message: STORE_CREDIT_PAYMENT_OVER_BALANCE,
          })
        )
      }

      // When we have a positive balance we also do not allow negative tendered amounts with store credits
      if (vn(amountToPay).isNeg()) {
        return $q.resolve(
          this.vdToastNotificationService.negative({
            message: STORE_CREDIT_INVALID_TENDERED_AMOUNT_ENTERED,
          })
        )
      }

      // If the tendered amount is greater than the available store credit,
      // allow the customer to make a partial payment
      if (vn(amountToPay).gt(availableStoreCredit)) {
        return this.paymentModalService
          .partialPayment('Store Credit', availableStoreCredit)
          .then((applyPartialPayment: boolean) => {
            if (applyPartialPayment) {
              return this.completePayment({
                paymentAmount: availableStoreCredit,
                paymentType,
              })
            }
          })
      }

      // Complete payment if none of the above conditions are met
      return this.completePayment({ paymentAmount: amountToPay, paymentType })

      // If we encounter a negative balance where the tendered amount is positive display notification
    } else if (hasNegativeBalance && !vn(this.amountTendered).isNeg()) {
      return $q.resolve(
        this.vdToastNotificationService.negative({
          message: STORE_CREDIT_INVALID_TENDERED_AMOUNT_ENTERED,
        })
      )
    } else {
      return this.completePayment({ paymentAmount: amountToPay, paymentType })
    }
  }

  /**
   * Process a loyalty payment
   *
   * @param {PaymentType} paymentType
   *        The payment type of this payment.
   *
   * @return {Promise} Resolved with completing the payment when valid or a toast notification
   */
  private payWithLoyalty(paymentType: ApiRetailerPaymentType) {
    const amountToPay = this.getPaymentAmountForCashDiscounting()
    const availableLoyalty = this.getAvailableLoyaltyBalance()

    if (vn(amountToPay).gt(getOutstandingBalance(this.sale))) {
      return $q.resolve(
        this.vdToastNotificationService.negative({
          message: LOYALTY_PAYMENT_OVER_BALANCE_MESSAGE,
        })
      )
    }

    // If the tendered amount is greater than the available loyalty balance,
    // allow the customer to make a partial payment
    if (vn(amountToPay).gt(availableLoyalty)) {
      return this.paymentModalService
        .partialPayment('Loyalty', availableLoyalty)
        .then((applyPartialPayment: any) => {
          if (applyPartialPayment) {
            return this.completePayment({
              paymentAmount: availableLoyalty,
              paymentType,
            })
          }
        })
    }

    // Complete payment if none of the above conditions are met
    return this.completePayment({ paymentAmount: amountToPay, paymentType })
  }

  /**
   * Determine the sale customer group
   *
   * @return {Promise} Resolved with the setting of the customer group
   */
  private resolveCustomerGroup(customer: ApiCustomer): IPromise<void> {
    // TODO: why do we need this?
    return this.customerManager
      .getCustomerGroup(customer)
      .then((group: ApiCustomerGroup) => {
        this.customerGroup = group
      })
      .catch((error: any) => {
        $log.warn('[paymentCtrl] Failed to lookup customer group', error)
      })
  }

  /**
   * Initialise the controller, setting up the initial state, binding events, etc.
   */
  private init() {
    if (this.sale && this.sale.customer) {
      this.resolveCustomerGroup(this.sale.customer)
    }

    // Hide the customer tooltip for lspay quick pay
    if ($stateParams.lspayQuickPaySelected) {
      this.$ngRedux.dispatch(hideAttachCustomerTooltip(true))
    }

    /**
     * Updates customer payment options based on logged in user
     *
     * @param  {Event} event
     *         The event object.
     */
    const loggedInUserChangeHandler = () => {
      this.$scope.$applyAsync(() => {
        this.buildPaymentOptions()
      })
    }

    // Update payment options on user change
    this.userManager.on('loggedInUserChange', loggedInUserChangeHandler, this)
    /**
     * Prints a receipt.
     *
     * @param  {Event} event
     *         The event object.
     */
    const printReceiptHandler = (event: PrintReceiptEvent = {}) => {
      if (event.isSignatureSlipReceipt === true) {
        const { paymentType, transaction } = event
        this.printSignatureSlipReceipt(paymentType, transaction)
      } else {
        this.paymentManager.updateReceiptMessageOnPrint(event.receiptHtmlExtra)
        this.printIntegratedPaymentReceipt()
      }
    }

    this.paymentManager.on('printReceipt', printReceiptHandler)

    /**
     * Updates the loyalty payment button config if we transition online/offline. Also refreshes the customer details
     * if we transition online.
     */
    const wrStatusChangeHandler = () => {
      this.updateLoyaltyButtonDisabledState()
      this.saleManager
        .updateCustomerBalances()
        .then(() => this.updateStoreCreditButtonDisabledState())
    }

    this.webRegisterStatusManager.on(
      EVENTS.statusChange,
      wrStatusChangeHandler,
      this
    )
    this.$scope.$watch('paymentCtrl.amountTendered', () => {
      this.updateLoyaltyButtonDisabledState()
      this.updateStoreCreditButtonDisabledState()
      this.validateTenderedAmount()
    })

    // When the customer is updated or removed from the sale, we need to updated the customer related payment options
    const unbindSaleCustomerUpdated = $rootScope.$on(
      EVENT.SALE_CUSTOMER_UPDATED,
      () => {
        this.fetchPaymentTypes().then(() => this.buildCustomerPaymentOptions())
      }
    )

    this.vdEscapeStack.register(this.closePaymentScreen)

    this.$scope.$on('$destroy', () => {
      this.userManager.off('loggedInUserChange', loggedInUserChangeHandler)
      this.paymentManager.off('printReceipt', printReceiptHandler)
      this.webRegisterStatusManager.off(
        EVENTS.statusChange,
        wrStatusChangeHandler
      )
      this.vdEscapeStack.deregister(this.closePaymentScreen)
      unbindSaleCustomerUpdated()
    })

    this.buildPaymentOptions().finally(() => {
      // If we have a payment to recover, we need to handle that first
      this.checkStartTransactionRecovery()

      // Handle the case for lspay quick pay
      if ($stateParams.lspayQuickPaySelected) {
        window.addEventListener('beforeunload', confirmQuit)
        this.lspayPayWithQuickPay()
      }
    })

    $rootScope.$emit('sale:payment')
    $rootScope.$on(EVENT.SALE_UPDATE_TENDERED_AMOUNT, () => {
      this.setTenderedAmount()
    })

    if (this.isPayPalEcomRefund) {
      this.paypalEcomPaymentType = this.sale.originalSalePayments.find(
        payment =>
          isPayPalEcomPaymentType(
            payment.paymentType.type_id,
            payment.paymentType.name
          )
      )?.paymentType
    }
  }

  /**
   * Pay with Lspay
   */
  private lspayPayWithQuickPay() {
    const lspayPaymentType =
      this.lspayPaymentType ||
      this.paymentTypes?.find(pt => pt.type_id === PAYMENT_TYPES.LSPAY)
    if (lspayPaymentType) {
      // Set paymentProcessing to true to prevent any other payment method from being processed
      // This can happen using the tab key to navigate through the payment methods
      // Setting the focusTrapActive to true will have the tab key only focus on the payment modal
      this.paymentProcessing = true

      return this.payWithLSPay(lspayPaymentType)
        .catch(() => {
          $log.warn(
            'PaymentCtrl: Failed to complete a quick pay transaction with LSPay'
          )
        })
        .finally(() => {
          this.paymentProcessing = false
          $window.removeEventListener('beforeunload', confirmQuit)
        })
    }
  }

  /**
   * Verify whether there's a need to recover a lspay transaction and initiate the recovery.
   */
  private checkStartTransactionRecovery() {
    const {
      intentId,
      registerSalePaymentId,
      amount: recoveryAmount,
    } = this.paymentManager.checkAndRetriveActivePayment(this.sale.localId)

    if (registerSalePaymentId) {
      const lspayPaymentType =
        this.lspayPaymentType ||
        this.paymentTypes?.find(pt => pt.type_id === PAYMENT_TYPES.LSPAY)
      if (lspayPaymentType) {
        // Set paymentProcessing to true to prevent any other payment method from being processed
        this.paymentProcessing = true

        return this.payWithLSPay(
          lspayPaymentType,
          intentId,
          registerSalePaymentId,
          recoveryAmount
        )
          .catch(() => {
            $log.warn(
              'PaymentCtrl: Failed to complete payment recovery for LSPay transaction'
            )
          })
          .finally(() => {
            this.paymentProcessing = false
            $window.removeEventListener('beforeunload', confirmQuit)
          })
      }
    }
  }

  /**
   * Fetch the payment types and build the various payment options based on those types, the current user, the type of
   * sale, the presence of a customer and general retailer settings.
   *
   * @return {Promise} A promise resolved once the payment options have been built
   */
  private buildPaymentOptions() {
    return $q
      .all([
        this.fetchPaymentTypes(),
        this.saleManager.updateCustomerBalances(),
      ])
      .then(() => this.buildCustomerPaymentOptions())
  }

  /**
   * Fetch the payment types for this retailer.
   * @deprecated this function should be removed once react version of payment screen is fully rolled out.
   * @return {Promise} A promise resolved once the payment types have been fetched
   */
  private fetchPaymentTypes(): IPromise<void> {
    if (!this.whenPaymentTypesFetched) {
      // Wait for payment types, then select every supported, non-deleted payment types
      this.whenPaymentTypesFetched = paymentTypesStore
        .all()
        .then(paymentTypes => {
          this.setSystemPaymentTypes(paymentTypes)
          if (this.isLSPayEcomRefund) {
            paymentTypes = preparePaymentTypesForLSpayEcomRefund(paymentTypes)
          }

          this.paymentTypes = this.filterPaymentTypesForDisplay(paymentTypes)
        })
    }
    return this.whenPaymentTypesFetched
  }

  /**
   * Defines the payment types so that we can call them directly.
   */
  private setSystemPaymentTypes(paymentTypes: ApiRetailerPaymentType[]) {
    for (const paymentType of paymentTypes) {
      if (paymentType.deleted_at) {
        continue
      }

      if (paymentType.type_id === PAYMENT_TYPES.STORE_CREDIT) {
        this.storeCreditPaymentType = paymentType
      }

      if (paymentType.type_id === PAYMENT_TYPES.ROUNDING) {
        this.roundingPaymentType = paymentType
      }

      if (paymentType.type_id === PAYMENT_TYPES.CURRENCY_ADJUST) {
        this.currencyAdjustmentPaymentType = paymentType
      }

      if (paymentType.type_id === PAYMENT_TYPES.LOYALTY) {
        this.loyaltyPaymentType = paymentType
      }
    }
  }

  /**
   * Returns a filtered collection of payment types that can be displayed on the pay screen
   *
   * @param {Array<PaymentType>} Payment types to filter
   *
   * @return {Array<PaymentType>} Filtered payment types
   */
  private filterPaymentTypesForDisplay(
    paymentTypes: ApiRetailerPaymentType[]
  ): ApiRetailerPaymentType[] {
    const trainingModeEnabled = this.trainingModeManager.trainingModeEnabled
    const paymentTypeControlsFeatureEnabled = this.vdFeatureManager.get(
      'payment_type_controls'
    )

    const activeOutletId = this.registerManager.getActiveOutlet().id

    // Payment types to exclude from the list of primary payment methods on the pay screen
    const EXCLUDED_PAYMENT_TYPES = [
      // Afterpay is no longer supported. All afterpay retailer payment types should be deleted.
      // Just to be safe we always exclude it from the pay screen
      PAYMENT_TYPES.AFTER_PAY,
      PAYMENT_TYPES.LOYALTY,
      PAYMENT_TYPES.XERO,
      PAYMENT_TYPES.STORE_CREDIT,
      PAYMENT_TYPES.ROUNDING,
      PAYMENT_TYPES.CURRENCY_ADJUST,
    ]

    if (trainingModeEnabled) {
      EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.GIFT_CARD)
      EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.WORLDPAY)
      EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.WORLDPAY_TRIPOS)
      EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.BIGCOMMERCE_GIFT_CERT)
      EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.LSPAY)
      EXCLUDED_PAYMENT_TYPES.push(...KLARNA_PAYMENT_TYPES)
    }

    if (!this.isLSPayEcomRefund) {
      EXCLUDED_PAYMENT_TYPES.push(PAYMENT_TYPES.LIGHTSPEED_ECOM)
    }

    let isLSPayFiltered = false

    return paymentTypes.filter(paymentType => {
      const isActive = !paymentType.deleted_at
      if (!isActive) {
        return false
      }

      const isDisabled = paymentType.disabled
      if (isDisabled) {
        return false
      }

      if (isIpadOnly(paymentType.payment_type)) {
        return false
      }

      if (EXCLUDED_PAYMENT_TYPES.includes(paymentType.type_id)) {
        return false
      }

      // Hide integrated payment types from training mode
      if (trainingModeEnabled && paymentType.config && paymentType.config.url) {
        return false
      }

      if (paymentTypeControlsFeatureEnabled) {
        // If the payment type is restricted by outlets, and the active outlet is not
        // one of these, filter the payment type
        if (!isEnabledPaymentTypeForOutlet(paymentType, activeOutletId)) {
          return false
        }
      }

      // Filter out only the first LSPay payment type from the payment types list.
      // Users may have multiple LSPay payment types, but we'll only show the first one at the top
      // of the list. All additional LSPay payment types will still be included in the payment types list
      // without any modifications.
      if (
        getFeatureFromCache(
          'raise_lspay_button_on_payment_screen_experiment'
        ) &&
        paymentType.type_id === PAYMENT_TYPES.LSPAY &&
        !isLSPayFiltered
      ) {
        // Store the LSPay payment type seperately to be used later
        this.lspayPaymentType = paymentType
        isLSPayFiltered = true
        return false
      }

      return true
    })
  }

  /**
   * Build the customer payment buttons - layby, onaccount, loyalty and store credits.
   * @note loyalty and store credit payment options still to come
   */
  private buildCustomerPaymentOptions() {
    const customerPaymentOptions = []
    const { displayStatus } = this.sale
    const hasNegativeBalance = vn(this.balance).isNeg()
    const hasValidCustomer = isValidCustomer(this.sale.customer)
    const userCanIssueStoreCredit = this.userManager.hasPermission(
      PERMISSIONS.STORE_CREDIT_ISSUE_RETURN
    )
    const notReturnSaleAndPositiveBalance =
      !isReturn(this.sale) && !hasNegativeBalance

    const addConfirmedStatusPayment = (
      status: SaleStatus.LAYBY_SALE | SaleStatus.ACCOUNT_SALE
    ) => {
      let isPaymentOptionDisabled: boolean

      if (status === SaleStatus.LAYBY_SALE) {
        isPaymentOptionDisabled =
          !hasValidCustomer ||
          isMarkedAsUnfulfilled(this.sale.status) ||
          isLegacyServiceSale(this.sale.status)
      } else if (status === SaleStatus.ACCOUNT_SALE) {
        const isSavedUnfulfilledWithPayments =
          this.sale.isSavedUnfulfilled && isPartialPaidSale(this.sale)
        const isRestrictedByOnAccountLimit =
          this.shouldEnforceOnAccountLimit &&
          (isOnAccountLimitDisabled(this.sale.customer) ||
            (!isOnAccountLimitUnlimited(this.sale.customer) &&
              isOnAccountLimitReached(this.sale)))

        isPaymentOptionDisabled =
          !hasValidCustomer ||
          isSavedUnfulfilledWithPayments ||
          isLegacyServiceSale(this.sale.status) ||
          isRestrictedByOnAccountLimit
      } else {
        isPaymentOptionDisabled = true
        $log.error('[paymentCtrl] Unsupported sale status', status)
      }

      customerPaymentOptions.push({
        id: status,
        label: this.wrSaleStatusFilter(status),
        action: () => this.laybyOnAccountSale(status),
        isDisabled: isPaymentOptionDisabled,
      })
    }
    let loyaltyButtonConfig

    // Layby, On Account, Loyalty not available on return sales
    if (notReturnSaleAndPositiveBalance) {
      if (this.loyaltyPaymentType) {
        loyaltyButtonConfig = {
          id: 'loyalty',
          label: this.loyaltyPaymentType.name,
          action: () => this.pay(this.loyaltyPaymentType!),
        } as const

        customerPaymentOptions.push(loyaltyButtonConfig)
      }
    }

    if (
      notReturnSaleAndPositiveBalance ||
      (this.hasXeroEnabled && isReturn(this.sale) && !hasNegativeBalance)
    ) {
      if (
        displayStatus !== SaleStatus.ACCOUNT_SALE &&
        !containsGiftCard(this.lineItems) &&
        this.userManager.hasPermission(PERMISSIONS.SALE_LAYBY)
      ) {
        addConfirmedStatusPayment(SaleStatus.LAYBY_SALE)
      }
    }

    if (
      this.isStoreCreditEnabled &&
      (!hasNegativeBalance || userCanIssueStoreCredit)
    ) {
      const storeCreditButtonConfig = {
        id: 'storeCredit',
        label: this.storeCreditPaymentType!.name,
        action: () => this.pay(this.storeCreditPaymentType!),
      } as const

      this.storeCreditButtonConfig = storeCreditButtonConfig
      this.updateStoreCreditButtonDisabledState()
      customerPaymentOptions.push(storeCreditButtonConfig)
    }

    if (notReturnSaleAndPositiveBalance || this.hasXeroEnabled) {
      if (
        displayStatus !== SaleStatus.LAYBY_SALE &&
        this.userManager.hasPermission(PERMISSIONS.SALE_ON_ACCOUNT)
      ) {
        addConfirmedStatusPayment(SaleStatus.ACCOUNT_SALE)
      }
    }

    this.loyaltyButtonConfig = loyaltyButtonConfig
    this.updateLoyaltyButtonDisabledState()

    this.customerPaymentOptions = customerPaymentOptions
  }

  /**
   * Used to check if the tendered amount is valid
   */
  private validateTenderedAmount() {
    // Using 'this.hasValidTenderedAmount' in the template as 'this.amountTendered' could be 0,
    // since 0 is falsy this would disable payment actions
    this.hasValidTenderedAmount = angular.isDefined(this.amountTendered)
    this.currentBalance = getOutstandingBalance(this.sale)

    if (!this.hasValidTenderedAmount) {
      return
    }

    // To prevent massive negative amount to pay
    if (vn(this.amountTendered).abs().gt(MAX_TENDERED_AMOUNT)) {
      this.hasValidTenderedAmount = false
    }

    const hasNegativeBalance = vn(this.currentBalance).isNeg()

    // Selected ABP should be paid in full so the min validation
    if (isAccountBalancePaymentAdded(this.sale.payAccountBalance)) {
      this.vendNumberOptions.min = vn(
        this.sale.payAccountBalance?.amount
      ).toNumber()
    }

    // Reset the min value validation in case of ABP removed from sale
    // or is paid
    if (
      !isAccountBalancePaymentAdded(this.sale.payAccountBalance) ||
      isAccountBalancePaymentPaid(this.sale.payAccountBalance)
    ) {
      this.vendNumberOptions.min = undefined
    }

    // Check that a valid number has been entered & that a negative balance is present
    // This section will only run for negative sales / returns
    if (this.hasValidTenderedAmount && hasNegativeBalance) {
      this.vendNumberOptions.min = this.currentBalance
    }
  }

  /**
   * Return the customers store credit balance
   *
   * @return Returns a string representing the customers store credit balance
   */
  private getAvailableStoreCredit(): number | string {
    if (
      !this.webRegisterStatusManager.isOnline() ||
      !this.sale.customerBalances
    ) {
      return 0
    }

    const storeCredit = this.sale.customerBalances.storeCredit
    const customerStoreCreditBalance = (storeCredit && storeCredit.balance) || 0
    const totalStoreCreditPartialPayments = this.customerBalancesManager.getTotalPartialPayments(
      PAYMENT_TYPES.STORE_CREDIT,
      this.sale
    )

    const storeCreditAvailable = subtract(
      customerStoreCreditBalance,
      totalStoreCreditPartialPayments
    )
    return round(storeCreditAvailable, 2, VendNumber.ROUNDING_MODES.ROUND_DOWN)
  }

  /**
   * Return the customers loyalty balance
   *
   * @return {String} Returns a string representing the customers loyalty balance
   */
  private getAvailableLoyaltyBalance(): string {
    const totalLoyaltyPartialPayments = this.customerBalancesManager.getTotalPartialPayments(
      PAYMENT_TYPES.LOYALTY,
      this.sale
    )
    const loyaltyBalance = this.sale.customer!.loyalty_balance

    return round(
      subtract(loyaltyBalance, totalLoyaltyPartialPayments),
      2,
      VendNumber.ROUND_DOWN
    )
  }

  /**
   * Update the disabled state of the store credit button. Store credit is
   * disabled if
   * - there is no customer
   * - when a positive balance is present but store credit is unavailable
   * - account balance payment is added to sale and store credit is less than the amount
   */
  private updateStoreCreditButtonDisabledState(): void {
    if (!this.storeCreditButtonConfig) {
      return
    }

    const hasNegativeBalance = vn(this.balance).isNeg()
    const hasValidCustomer = isValidCustomer(this.sale.customer)

    if (hasValidCustomer) {
      const availableStoreCredit = this.getAvailableStoreCredit()

      this.storeCreditButtonConfig.isDisabled = false

      // The store credit button is enabled for both negative and positive balances. If we use it with a negative
      // balance the customer will be credited. If you want to spend store credit you have to be on a positive balance
      // sale and have store credit available.
      if (vn(availableStoreCredit).lte(0) && !hasNegativeBalance) {
        this.storeCreditButtonConfig.isDisabled = true
        return
      }
      if (
        !hasNegativeBalance &&
        !(this.sale.customerBalances && this.sale.customerBalances.storeCredit)
      ) {
        this.storeCreditButtonConfig.isDisabled = true
      }

      // Disable store credit if Klarna payments have been made on the sale.
      if (
        hasKlarnaPayments({
          payments: this.sale.originalSalePayments,
        })
      ) {
        this.storeCreditButtonConfig.isDisabled = true
      }

      if (
        isAccountBalancePaymentAdded(this.sale.payAccountBalance) &&
        vn(this.sale.payAccountBalance!.amount).gt(availableStoreCredit)
      ) {
        this.storeCreditButtonConfig.isDisabled = true
      }
    } else {
      this.storeCreditButtonConfig.isDisabled = true
    }
  }

  /**
   * Update the disabled state of the loyalty button.
   * Loyalty is disabled if
   * - there is no customer
   * - the customer has loyalty disabled (or a loyalty balance of 0)
   * - the amount tendered is $0 or less
   * - the client is offline.
   * - the account balance payment is added but not paid (can't pay ABP by loyalty)
   */
  private updateLoyaltyButtonDisabledState() {
    if (!this.loyaltyButtonConfig) {
      return
    }

    const isOnline = this.webRegisterStatusManager.isOnline()
    const loyaltyAvailable = this.sale.customer
      ? hasValidCustomerWithLoyalty(this.sale.customer)
      : false

    if (isOnline && loyaltyAvailable) {
      const availableLoyaltyBalance = this.getAvailableLoyaltyBalance()
      this.loyaltyButtonConfig.isDisabled = false

      if (
        vn(availableLoyaltyBalance).lte(0) ||
        vn(this.amountTendered).lte(0) ||
        isABPAddedButNotPaid(this.sale)
      ) {
        this.loyaltyButtonConfig.isDisabled = true
      }
    } else {
      this.loyaltyButtonConfig.isDisabled = true
    }
  }

  /**
   * Pulls up the print dialog using the receipt html extra set on the receipt manager to injected into the receipt.
   */
  private printIntegratedPaymentReceipt() {
    $rootScope.$emit(EVENT.PRINT, {
      type: 'receipt-settings-and-template',
      data: {
        trainingModeEnabled: this.trainingModeManager.trainingModeEnabled,
        sale: selectCurrentSaleAsSale(this.$ngRedux.getState()),
        receiptHtmlExtra: this.paymentManager.paymentIntegrationReceiptMessage,
        register: this.registerManager.getActiveRegister(),
        outlet: this.registerManager.getActiveOutlet(),
      },
    })
  }

  /**
   * Pulls up the print dialog using the payment data set on the receipt manager to injected into the receipt.
   *
   * @param  {PaymentType} paymentType
   *         The payment type of the sale.
   *
   * @param  {Object} transaction
   *         The transaction data of the sale.
   */
  private printSignatureSlipReceipt(
    paymentType?: ApiRetailerPaymentType,
    transaction?: ReceiptTransaction
  ) {
    const paymentData = {
      isSignatureSlipReceipt: true,
      paymentType,
      transaction,
    }
    $rootScope.$emit(EVENT.PRINT, {
      type: 'receipt-settings-and-template',
      data: {
        trainingModeEnabled: this.trainingModeManager.trainingModeEnabled,
        sale: selectCurrentSaleAsSale(this.$ngRedux.getState()),
        paymentData,
        register: this.registerManager.getActiveRegister(),
        outlet: this.registerManager.getActiveOutlet(),
      },
    })
  }

  /**
   * Pulls up the print dialog if there is a receipt_html_extra property send through on the event data payload, prior
   * to completing the sale.
   *
   * @param  {PaymentType} option.paymentType
   *         The payment type of this payment.
   *
   * @param {String} params.registerId
   *         The id of the current register.
   *
   * @param  {Object} option.eventData
   *         The payment gateway event data object.
   */
  private handleAcceptIntegratedPayment({
    currency,
    paymentType,
    registerId,
    eventData,
  }: {
    currency: string
    paymentType: ApiRetailerPaymentType
    registerId: string
    eventData: any
  }) {
    if (eventData && eventData.receipt_html_extra) {
      this.paymentManager.acceptedPaymentIntegrationReceiptMessages.push(
        eventData.receipt_html_extra
      )
    }

    trackIntegratedPaymentStep({
      paymentType,
      eventData,
    })

    let isPartialAuth = false
    let tipAmount = null
    let approvedAmount = this.amountTendered

    // If the approved_amount is less than the tendered amount then
    // they have made a partial payment
    if (
      eventData.approved_amount &&
      vn(this.amountTendered).gt(vn(eventData.approved_amount))
    ) {
      approvedAmount = eventData.approved_amount
      isPartialAuth = true
    }

    // If the tipping feature is enabled, AND
    // If the approved_amount is more than the tendered amount then
    // we assume they have added a tip to the payment
    if (
      this.vdFeatureManager.get('terminal_tipping') &&
      eventData.approved_amount &&
      vn(this.amountTendered).lt(vn(eventData.approved_amount))
    ) {
      approvedAmount = eventData.approved_amount
      tipAmount = subtract(eventData.approved_amount, this.amountTendered)
    }

    // should complete the payment no matter the partial auth will be accepted or reversed
    this.completePayment({
      paymentAmount: approvedAmount,
      tipAmount,
      paymentType,
      transactionData: extractExternalPaymentData(eventData),
      paymentId: eventData.payment_id,
    }).then(() => {
      if (isPartialAuth) {
        this.paymentModalService
          .partialAuth(approvedAmount)
          .then((applyPartialAuth: boolean) => {
            if (!applyPartialAuth) {
              const externalTransactionIds = []
              if (eventData.external_transaction_id) {
                externalTransactionIds.push(eventData.external_transaction_id)
              }
              this.amountTendered = -approvedAmount
              return this._payWithIntegration({
                currency,
                paymentType,
                registerId,
                externalTransactionIds,
              })
            }
          })
      }
    })
  }

  /**
   * Pulls up the print dialog if there is a receipt_html_extra property send through on the event data payload.
   *
   * @param  {PaymentType} option.paymentType
   *         The payment type of this payment.
   *
   * @param  {Object} option.eventData
   *         The payment gateway event data object.
   */
  private handleDeclineIntegratedPayment({
    paymentType,
    eventData,
  }: {
    paymentType: ApiRetailerPaymentType
    eventData: any
  }) {
    if (eventData && eventData.receipt_html_extra) {
      this.paymentManager.updateReceiptMessageOnDecline(
        eventData.receipt_html_extra
      )
      this.printIntegratedPaymentReceipt()
    }

    trackIntegratedPaymentStep({ paymentType, eventData })
  }

  /**
   * Shows the Swiper payment modal and initiates the payment process. Adds the identifying EMV data for the
   * transaction onto the receipt.
   *
   * @param  {PaymentType} paymentType
   *         The payment type of this payment.
   *
   * @return {Promise} Resolved once the payment process has been successful and the payment is completed.
   */
  private payWithSwiper(paymentType: ApiRetailerPaymentType) {
    this.vdEscapeStack.deregister(this.closePaymentScreen)

    if (
      !this.webRegisterStatusManager.isOnline() ||
      !this.vdWindowDelegate.isOnline
    ) {
      this.Confirm({
        header: 'You’re offline',
        content: `<p class="vd-p">${paymentType.name} payments can not be accepted while the register is offline.</p>`,
        cancel: {
          isVisible: false,
        },
        confirm: {
          label: 'Choose Another Payment Type',
        },
      })

      this.vdEscapeStack.register(this.closePaymentScreen)
      return $q.reject()
    }
    const currency = this.registerManager.getActiveOutlet().currency
    let originalRegisterSalePaymentId

    if (isReturn(this.sale)) {
      const originalRegisterSalePayments = getAllPaymentsForPaymentType({
        payments: this.sale.originalSalePayments,
        paymentTypeId: paymentType.type_id,
      })

      originalRegisterSalePaymentId = originalRegisterSalePayments.length
        ? originalRegisterSalePayments[0].id
        : null
    }

    return this.paymentModalService
      .swiperPayment({
        amount: vn(this.amountTendered).toNumber(),
        paymentType,
        originalRegisterSalePaymentId,
        currency,
      })
      .then((transaction: any) =>
        this.handleSwiperPaymentSuccess({ transaction, paymentType })
      )
      .finally(() => this.vdEscapeStack.register(this.closePaymentScreen))
  }

  /**
   * Handles a successful swiper payment transaction.
   *
   * @param {Object} params
   *
   *        @param {Object} params.transaction
   *                The complete transaction as returned from the api.
   *
   *        @param {Object} params.paymentType
   *               The identifying payment type.
   *
   * @return {Promise} Resolved once the swiper payment has been completed.
   */
  private handleSwiperPaymentSuccess({
    transaction,
    paymentType,
  }: {
    transaction: ApiTransaction
    paymentType: ApiRetailerPaymentType
  }) {
    if (!transaction || !transaction.tenders) {
      trackError('[Payment] Missing required Swiper transaction details', {
        data: { transaction },
      })
      return $q.reject()
    }

    const mainTender = getMainApiTender(transaction.tenders)

    if (!mainTender) {
      trackError('[Payment] Missing main tender from swiper transaction')
      return $q.reject()
    }

    this.vdTracking.trackUserEvent({
      eventName: 'Swiper Payment Completed',
      metadata: {
        paymentTypeId: paymentType.type_id,
        transactionId: transaction.id,
        amount: this.amountTendered,
      },
    })

    const transactionData = {
      // @note that the register_sale_payment_id is the same as the transactionId (uuid) that is generated within the
      // swiper payment modal controller when performing the inital swiper request.
      source: SWIPER_SOURCE_NAME,
      source_id: transaction.id,
      payment_id: transaction.register_sale_payment_id,
      external_transaction_id: mainTender.external_transaction_id,
      signature_url: mainTender.provider_fields.signature_url
        ? mainTender.provider_fields.signature_url
        : null,
      transaction_status: mainTender.status,
      card: {
        entry_method: mainTender.card_details.entry_method,
        brand: mainTender.card_details.card?.brand,
        type: mainTender.card_details.card?.type,
        last_four: mainTender.card_details.card?.last_four,
      },
      emv: mainTender.emv || null,
      provider_fields: mainTender.provider_fields,
    }

    let paymentAmount: number
    let tipAmount = 0
    let surchargeAmount = 0
    if (mainTender.type === 'REFUND') {
      paymentAmount = -mainTender.amount
    } else {
      paymentAmount = getTotalPaymentAmountFromTenders(transaction.tenders)
      tipAmount = getTotalTipAmountFromTenders(transaction.tenders)
      surchargeAmount =
        this.vdFeatureManager.get('lspay_surcharge') === true
          ? getTotalSurchargeAmountFromTenders(transaction.tenders)
          : 0
    }

    const surchargeRule = surchargeAmount
      ? getSurchargeRuleFromTenders(transaction.tenders)
      : ''

    const surchargeData = {
      amount: surchargeAmount,
      rule: surchargeRule,
    }

    return this.completePayment({
      paymentAmount,
      paymentType,
      transactionData,
      tipAmount,
      surchargeData,
    })
  }

  /**
   * Shows the LSPAY payment modal and initiates the payment process.
   *
   * @param  {PaymentType} paymentType
   *         The payment type of this payment.
   *
   * @return {Promise} Resolved once the payment process has been successful and the payment is completed.
   */
  private payWithLSPay(
    paymentType: ApiRetailerPaymentType,
    intentId?: string,
    transactionId?: string,
    recoveryAmount?: string | number
  ) {
    this.vdEscapeStack.deregister(this.closePaymentScreen)

    if (
      !this.webRegisterStatusManager.isOnline() ||
      !this.vdWindowDelegate.isOnline
    ) {
      this.Confirm({
        header: 'You’re offline',
        content: `<p class="vd-p">${paymentType.name} payments can not be accepted while the register is offline.</p>`,
        cancel: {
          isVisible: false,
        },
        confirm: {
          label: 'Choose Another Payment Type',
        },
      })

      this.vdEscapeStack.register(this.closePaymentScreen)
      return $q.reject()
    }

    const activeOutlet = this.registerManager.getActiveOutlet()

    const modalOptions: LSPAYModalOptions = {
      amount: vn(recoveryAmount || this.amountTendered).toNumber(),
      outletId: activeOutlet.id,
      currency: activeOutlet.currency,
      register: this.registerManager.getActiveRegister(),
      registerSaleId: this.sale.localId,
      retailerPaymentType: paymentType,
      transactionId: transactionId || uuid(),
      paymentIntentId: intentId,
      isRecovery: !!transactionId,
      withSurcharge:
        getFeatureFromCache('lspay_surcharge') &&
        canApplySurcharge(
          this.amountTendered,
          {
            ...this.sale,
            status: this.sale.status as SaleStatus,
          },
          this.hasQboEnabled,
          this.hasXeroEnabled,
          getFeatureFromCache('lspay_surcharge_xero_onaccount'),
          getFeatureFromCache('lspay_surcharge_qbo_onaccount')
        ),
      ...(getFeatureFromCache('lspay_tipping_on_pre_tax_amount') &&
        !isReturn(this.sale) &&
        !isBlindReturn(this.sale) && {
          tipEligibleAmount: vn(
            round(getTipEligibleAmount(this.amountTendered, this.sale), 2)
          ).toNumber(),
        }),
    }

    if (isReturn(this.sale)) {
      const originalRegisterSalePayments = getAllPaymentsForPaymentType({
        payments: this.sale.originalSalePayments,
        paymentTypeId: paymentType.type_id,
      })

      modalOptions.originalRegisterSalePaymentIds = getPaymentIds(
        originalRegisterSalePayments
      )
      modalOptions.originalSalePaidByOtherPT = hasPaymentsForOtherPaymentType({
        payments: this.sale.originalSalePayments,
        paymentTypeId: paymentType.type_id,
      })
    }

    return this.paymentModalService
      .lspayPayment(modalOptions)
      .then((transaction: ApiTransaction) =>
        this.handleSwiperPaymentSuccess({
          transaction,
          paymentType,
        })
      )
      .finally(() => this.vdEscapeStack.register(this.closePaymentScreen))
  }

  /**
   * Shows the LSPAY/PAYPAL Ecom refund modal and initiates the refund process.
   *
   * @param  {PaymentType} paymentType
   *         The payment type of this payment.
   *
   * @return {Promise} Resolved once the refund process has been successful and the refund is completed.
   */
  private refundWithEcom(paymentType: ApiRetailerPaymentType) {
    this.vdEscapeStack.deregister(this.closePaymentScreen)

    if (
      !this.webRegisterStatusManager.isOnline() ||
      !this.vdWindowDelegate.isOnline
    ) {
      this.Confirm({
        header: 'You’re offline',
        content: `<p class="vd-p">${paymentType.name} payments can not be accepted while the register is offline.</p>`,
        cancel: {
          isVisible: false,
        },
        confirm: {
          label: 'Choose Another Payment Type',
        },
      })

      this.vdEscapeStack.register(this.closePaymentScreen)
      return $q.reject()
    }

    this.vdTracking.trackUserEvent({
      eventName: 'Clicked: LS Payment(online) button',
      metadata: {
        saleId: this.sale.localId,
      },
    })
    // return rejected promise if the sale missing returnFor which represent the original sale id,
    // can't perform refund without originalSaleId
    if (!this.sale.returnFor) {
      return $q.reject()
    }

    const modalOptions: EcomRefundModalOptions = {
      amount: vn(this.amountTendered).toNumber(),
      originalSaleId: this.sale.returnFor,
      registerId: this.sale.registerId || '',
      registerSalePaymentId: uuid(),
    }

    return this.paymentModalService
      .ecomRefund(modalOptions)
      .then(() => {
        this.completePayment({
          paymentType,
          transactionData: {
            payment_id: modalOptions.registerSalePaymentId,
          },
        })
      })
      .finally(() => this.vdEscapeStack.register(this.closePaymentScreen))
  }

  /**
   * For when cash discounting is enabled and when paying with a Vend internal payment type. We check to see if the
   * amount tendered can be adjusted to be the outstanding "cash" balance - since we treat Store Credit, Loyalty and
   * Gift Card as cash (there is no service fee for these payment types).
   *
   * @returns {Number} The amount that can be paid
   */
  private getPaymentAmountForCashDiscounting() {
    const tenderedAmountMatchesBalance = vn(this.amountTendered).eq(
      getOutstandingBalance(this.sale)
    )
    const hasCashDiscounting = this.registerManager.hasCashDiscounting()

    // Only adjust the amount tendered when Cash Discounting is enabled, the sale has a positive service fee and the
    // amount tendered matches the outstanding balance
    if (
      hasCashDiscountingServiceFee(hasCashDiscounting, this.sale) &&
      tenderedAmountMatchesBalance
    ) {
      return this.saleManager.getCashDiscountedOutstandingBalance()
    } else {
      return this.amountTendered
    }
  }

  /**
   * Sets the `amountTendered` and `balance` of the current sale
   */
  private setTenderedAmount(amount?: number | string) {
    this.balance = getOutstandingBalance(this.sale)

    if (amount) {
      this.amountTendered = amount
    } else {
      // The input is prefilled with the remaining amount to pay
      this.amountTendered = this.balance
    }
  }

  /**
   * Returns the remaining amount if the amountTendered is paid
   *
   * @returns {Number} The amount that is remaining if amountTendered is paid
   */
  public getRemainingAmountIfTendered() {
    if (!isFinite(this.currentBalance) || !isFinite(this.amountTendered)) {
      return 0
    }

    return subtract(this.currentBalance!, this.amountTendered)
  }

  /**
   * Determine whether the amount is a valid customer payment
   *
   * @return {Boolean} false for loyalty and store credit payment.
   * true for
   * - on account / layby
   * - if it is a partial payment and not the final payment of the sale
   * - if ABP is added and not paid
   */
  public invalidCustomerPaymentAmount(customerPaymentStatus: SaleStatus) {
    if (
      customerPaymentStatus !== SaleStatus.LAYBY_SALE &&
      customerPaymentStatus !== SaleStatus.ACCOUNT_SALE
    ) {
      return false
    }

    if (isABPAddedButNotPaid(this.sale)) {
      return true
    }

    return this.isPartialPayment()
  }

  /**
   * Determine whether the current payment is a partial payment
   *
   * @return {Boolean} true if partial payment, false otherwise
   */
  public isPartialPayment() {
    return (
      this.getRemainingAmountIfTendered() > 0 &&
      getOutstandingBalance(this.sale) > 0
    )
  }

  /**
   * Determine what message (if any) to show underneath the customer payment types.
   *
   * @return the message to show or null if no message should be visible i.e. customer payment
   * types are not disabled.
   */
  public customerPaymentTypeMessage() {
    const sale = this.sale

    if (isABPAddedButNotPaid(this.sale)) {
      const currencySymbol = this.registerManager.getActiveOutlet()
        .currency_symbol
      return `These payment types can’t be used for Account Balance Payments. Take a payment for the full ${currencySymbol}${
        sale.payAccountBalance!.amount
      } Account Balance Payment, then they can be used for the remainder.`
    } else if (
      isMarkedAsUnfulfilled(this.sale.status) ||
      isLegacyServiceSale(sale.status)
    ) {
      if (isMarkedAsUnfulfilled(this.sale.status)) {
        if (
          (sale.isSavedUnfulfilled || this.isPartialPayment()) &&
          hasPayments(this.sale)
        ) {
          return 'Layby and On Account can’t be used for unfulfilled sales'
        }
        return 'Layby can’t be used for unfulfilled sales'
      }
      return 'Layby and On Account can’t be used for service sales'
    } else if (
      isValidCustomer(this.sale.customer) &&
      this.hasValidTenderedAmount &&
      this.isPartialPayment()
    ) {
      return `Layby and On Account can’t be used for partial payments`
    } else if (containsGiftCard(this.lineItems)) {
      return `Layby can’t be used with gift cards`
    } else if (
      this.shouldEnforceOnAccountLimit &&
      isValidCustomer(sale.customer) &&
      !isLayby(sale.status)
    ) {
      const canUpdateOnAccountLimit = this.userManager.hasPermission(
        PERMISSIONS.CUSTOMER_ON_ACCOUNT_LIMIT_ADD_EDIT
      )
      if (isOnAccountLimitDisabled(sale.customer) && canUpdateOnAccountLimit) {
        return 'On account payments are disabled for this customer. Select a different payment method or enable on account payments.'
      }
      if (isOnAccountLimitDisabled(sale.customer) && !canUpdateOnAccountLimit) {
        return 'On account payments are disabled for this customer. Select a different payment method or talk to your manager/admin.'
      }

      if (
        !isOnAccountLimitUnlimited(sale.customer) &&
        isOnAccountLimitReached(this.sale) &&
        canUpdateOnAccountLimit
      ) {
        return 'This customer reached their on account limit. Select a different payment method or increase the limit.'
      }
      if (
        !isOnAccountLimitUnlimited(sale.customer) &&
        isOnAccountLimitReached(this.sale) &&
        !canUpdateOnAccountLimit
      ) {
        return 'This customer reached their on account limit. Select a different payment method or talk to your manager/admin.'
      }
    }
    return null
  }

  /**
   * Determine if the Save Sale button for unfulfilled sales / service should be shown ~ to allow
   * for partially paid unfulfilled sales.
   * @deprecated This method is deprecated and will be removed once payment panel is migrated to react.
   * If you are changing this method, please check the react version of this method in react version
   * @return {Boolean} true if the save sale button should be visible, false otherwise
   */
  public showSaveButton() {
    const saleHasServiceOrder = hasServiceOrder(
      this.sale.serviceFields,
      this.sale.customFields
    )
    const hideSaveBtnForServices =
      saleHasServiceOrder &&
      !isLegacyServiceSale(this.sale.status) &&
      !isMarkedAsUnfulfilled(this.sale.status)

    return Boolean(
      !this.isBlockedByAccountingIntegrations &&
        (isPartialPaidSale(this.sale) || isUnpaidSale(this.sale)) &&
        !hideSaveBtnForServices
    )
  }

  /**
   * Returns the payment button hot key option for the given index.
   */
  public getPaymentButtonHotKeyOption(index: number) {
    // The maximum index for payment options which can have hotkey option.
    // If lspayPaymentType is present, the max index is 8 (options start from 2).
    // Otherwise, the max index is 9 (options start from 1)
    const maxIndex = this.lspayPaymentType ? 8 : 9

    if (index < maxIndex) {
      const hotKeyValue = this.lspayPaymentType ? index + 2 : index + 1
      return {
        combo: 'alt+' + hotKeyValue,
        summary: 'Select payment option ' + index,
        tip: hotKeyValue,
        count: true,
      }
    }

    return null
  }

  /**
   * Calculate the deposit amount for a partially paid unfulfilled sale.
   */
  public depositAmount() {
    return isAccountBalancePaymentPaid(this.sale.payAccountBalance)
      ? subtract(this.paymentsTotal, this.sale.payAccountBalance!.amount)
      : this.paymentsTotal
  }

  /**
   * Show the swiper confirmation modal for affected PTs
   */
  private showSwiperMaintenanceModal() {
    this.Confirm({
      header: `Unable to process payment right now`,
      content: `<p class="vd-p">This payment can't be processed right now. Lightspeed Retail is undergoing required maintenance which will take up to three minutes.</p>`,
      cancel: {
        isVisible: false,
      },
      confirm: {
        label: 'Dismiss',
      },
    })

    this.vdEscapeStack.register(this.closePaymentScreen)
    return
  }

  public hasCashDiscountingServiceFee() {
    return this.saleManager.hasCashDiscountingServiceFee()
  }

  public getCashDiscountedOutstandingBalance() {
    return this.saleManager.getCashDiscountedOutstandingBalance()
  }

  public isAccountBalancePaymentAdded() {
    return isAccountBalancePaymentAdded(this.sale.payAccountBalance)
  }

  public saleHasValidCustomer() {
    return isValidCustomer(this.sale.customer)
  }

  public isPartialPaidSale() {
    return isPartialPaidSale(this.sale)
  }

  public isUnpaidSale() {
    return isUnpaidSale(this.sale)
  }
}

/**
 * Controller for the payment view
 *
 * @module webregister.controllers.PaymentCtrl
 */
angular.module('webregister.controllers').controller('PaymentCtrl', PaymentCtrl)
